import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Modal, Badge, ButtonGroup, Button } from 'react-bootstrap';

/**
 * EnhancedNetworkGraph - Advanced D3.js network visualization
 *
 * Features:
 * - Hierarchical beacon tree layout
 * - Animated traffic flow
 * - Beacon type badges (HTTP/DNS/SMB)
 * - Real-time health indicators
 * - Interactive zoom/pan
 * - Click nodes for details
 */
const EnhancedNetworkGraph = ({ beacons = [], listeners = [], redirectors = [], onNodeClick, show = false, onHide }) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const [layout, setLayout] = useState('force'); // 'force', 'tree', 'radial'
  const [showTraffic, setShowTraffic] = useState(true);
  const [showRedirectors, setShowRedirectors] = useState(true);

  useEffect(() => {
    if (!show) return;

    // Wait for modal to render and ref to be available
    const timer = setTimeout(() => {
      if (svgRef.current && (beacons.length > 0 || listeners.length > 0)) {
        drawGraph();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [beacons, listeners, redirectors, layout, show, showRedirectors]);

  const drawGraph = () => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    // Add dark background
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', '#1f2937');

    // Create zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    const g = svg.append('g');

    // Prepare data
    const nodes = prepareNodes(beacons, listeners, redirectors, showRedirectors);
    const links = prepareLinks(beacons, listeners, redirectors, showRedirectors);

    // Create simulation based on layout
    let simulation;
    if (layout === 'force') {
      simulation = createForceSimulation(nodes, links, width, height);
    } else if (layout === 'tree') {
      applyTreeLayout(nodes, links, width, height);
    } else if (layout === 'radial') {
      applyRadialLayout(nodes, links, width, height);
    }

    // Draw links
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', d => getLinkColor(d))
      .attr('stroke-width', d => d.bandwidth || 2)
      .attr('stroke-opacity', 0.6)
      .attr('marker-end', 'url(#arrowhead)');

    // Add link labels
    const linkLabel = g.append('g')
      .attr('class', 'link-labels')
      .selectAll('text')
      .data(links)
      .join('text')
      .attr('class', 'link-label')
      .attr('font-size', 10)
      .attr('fill', '#9ca3af')
      .text(d => d.protocol || '');

    // Draw nodes
    const node = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('class', 'node')
      .call(drag(simulation));

    // Node circles with enhanced styling
    node.append('circle')
      .attr('r', d => d.radius || 20)
      .attr('fill', d => {
        // Unapproved beacons: dark/dimmed
        if (d.data && d.data.isApproved === false) return '#374151';
        // Offline beacons: grayscale
        if (!d.isAlive && d.type !== 'server') return '#4b5563';
        return getNodeColor(d);
      })
      .attr('stroke', d => {
        // Server: always blue border
        if (d.type === 'server') return '#60a5fa';
        // Unapproved: orange warning
        if (d.data && d.data.isApproved === false) return '#f59e0b';
        // Active: green
        if (d.isAlive) return '#10b981';
        // Offline: red
        return '#ef4444';
      })
      .attr('stroke-width', d => {
        // Thicker border for unapproved (needs attention)
        if (d.data && d.data.isApproved === false) return 4;
        return 3;
      })
      .attr('stroke-dasharray', d => {
        // Dashed border for unapproved
        if (d.data && d.data.isApproved === false) return '5,3';
        return null;
      })
      .attr('class', d => {
        if (d.data && d.data.isApproved === false) return 'node-unapproved';
        return d.isAlive ? 'node-alive' : 'node-dead';
      });

    // Node icons (FontAwesome)
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 5)
      .attr('font-size', 20)
      .attr('font-family', 'FontAwesome')
      .attr('fill', 'white')
      .text(d => getNodeIcon(d));

    // Node labels (white for dark theme)
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 35)
      .attr('font-size', 12)
      .attr('font-weight', 'bold')
      .attr('fill', '#ffffff')
      .attr('stroke', '#000000')
      .attr('stroke-width', 0.5)
      .attr('paint-order', 'stroke')
      .text(d => d.label);

    // Node type badges
    node.append('rect')
      .attr('x', -20)
      .attr('y', -35)
      .attr('width', 40)
      .attr('height', 16)
      .attr('rx', 8)
      .attr('fill', d => getTypeBadgeColor(d))
      .attr('opacity', 0.9);

    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', -23)
      .attr('font-size', 10)
      .attr('font-weight', 'bold')
      .attr('fill', 'white')
      .text(d => (d.type || 'HTTP').toUpperCase());

    // Evasion badge (if AMSI/ETW patched)
    node.filter(d => d.evasion && (d.evasion.amsi || d.evasion.etw))
      .append('circle')
      .attr('cx', 20)
      .attr('cy', -20)
      .attr('r', 8)
      .attr('fill', '#10b981')
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    node.filter(d => d.evasion && (d.evasion.amsi || d.evasion.etw))
      .append('text')
      .attr('x', 20)
      .attr('y', -17)
      .attr('text-anchor', 'middle')
      .attr('font-size', 10)
      .attr('font-family', 'FontAwesome')
      .attr('fill', 'white')
      .text('\uf00c'); // fa-check

    // Click handler
    node.on('click', (event, d) => {
      event.stopPropagation();
      if (onNodeClick) {
        onNodeClick(d);
      }
    });

    // Hover effects
    node.on('mouseover', function(event, d) {
      d3.select(this).select('circle')
        .transition()
        .duration(200)
        .attr('r', (d.radius || 20) * 1.3);

      showTooltip(event, d);
    })
    .on('mouseout', function() {
      d3.select(this).select('circle')
        .transition()
        .duration(200)
        .attr('r', d => d.radius || 20);

      hideTooltip();
    });

    // Arrow marker
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#0066cc');

    // Traffic animation
    if (showTraffic) {
      animateTraffic(g, links);
    }

    // Add legend
    const legend = svg.append('g')
      .attr('transform', `translate(20, 20)`);

    const legendItems = [
      { type: 'server', label: 'C2 Server', color: '#003d82' },
      { type: 'redirector', label: 'Redirector', color: '#FF9800' },
      { type: 'listener', label: 'Listener', color: '#10b981' },
      { type: 'https', label: 'HTTP/HTTPS Beacon', color: '#10b981' },
      { type: 'dns', label: 'DNS Beacon', color: '#8b5cf6' },
      { type: 'smb', label: 'SMB Beacon', color: '#f59e0b' },
      { type: 'status-online', label: 'Online', color: '#10b981', border: true },
      { type: 'status-offline', label: 'Offline', color: '#4b5563', border: '#ef4444' },
      { type: 'status-unapproved', label: 'Awaiting Approval', color: '#374151', border: '#f59e0b', dashed: true }
    ];

    legendItems.forEach((item, i) => {
      const legendItem = legend.append('g')
        .attr('transform', `translate(0, ${i * 25})`);

      legendItem.append('circle')
        .attr('cx', 10)
        .attr('cy', 0)
        .attr('r', 8)
        .style('fill', item.color)
        .style('stroke', item.border === true ? '#10b981' : (item.border || '#fff'))
        .style('stroke-width', item.border ? 2 : 1)
        .style('stroke-dasharray', item.dashed ? '3,3' : null);

      legendItem.append('text')
        .attr('x', 25)
        .attr('y', 4)
        .style('font-size', '12px')
        .style('fill', '#e5e7eb')
        .text(item.label);
    });

    // Update positions on tick
    if (simulation) {
      simulation.on('tick', () => {
        link
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y);

        linkLabel
          .attr('x', d => (d.source.x + d.target.x) / 2)
          .attr('y', d => (d.source.y + d.target.y) / 2);

        node.attr('transform', d => `translate(${d.x},${d.y})`);
      });
    } else {
      // Static layout (tree/radial) - manually resolve source/target node references
      link
        .attr('x1', d => {
          const sourceNode = nodes.find(n => n.id === (typeof d.source === 'object' ? d.source.id : d.source));
          return sourceNode ? sourceNode.x : 0;
        })
        .attr('y1', d => {
          const sourceNode = nodes.find(n => n.id === (typeof d.source === 'object' ? d.source.id : d.source));
          return sourceNode ? sourceNode.y : 0;
        })
        .attr('x2', d => {
          const targetNode = nodes.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));
          return targetNode ? targetNode.x : 0;
        })
        .attr('y2', d => {
          const targetNode = nodes.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));
          return targetNode ? targetNode.y : 0;
        });

      linkLabel
        .attr('x', d => {
          const sourceNode = nodes.find(n => n.id === (typeof d.source === 'object' ? d.source.id : d.source));
          const targetNode = nodes.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));
          return sourceNode && targetNode ? (sourceNode.x + targetNode.x) / 2 : 0;
        })
        .attr('y', d => {
          const sourceNode = nodes.find(n => n.id === (typeof d.source === 'object' ? d.source.id : d.source));
          const targetNode = nodes.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));
          return sourceNode && targetNode ? (sourceNode.y + targetNode.y) / 2 : 0;
        });

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    }
  };

  const prepareNodes = (beacons, listeners, redirectors, showRedirectors) => {
    const nodes = [];

    // C2 Server (root node)
    nodes.push({
      id: 'c2-server',
      label: 'C2 Server',
      type: 'server',
      radius: 30,
      fx: null, // Allow movement
      fy: null
    });

    // Listeners
    listeners.forEach(listener => {
      nodes.push({
        id: `listener-${listener._id}`,
        label: listener.name,
        type: listener.type,
        isAlive: listener.isActive,
        healthStatus: listener.healthStatus,
        port: listener.config?.port,
        radius: 25,
        data: listener
      });
    });

    // Redirectors (if enabled)
    if (showRedirectors && redirectors) {
      redirectors.forEach(redirector => {
        nodes.push({
          id: `redirector-${redirector._id}`,
          label: redirector.name,
          hostname: redirector.hostname,
          type: 'redirector',
          isAlive: redirector.health?.isOnline,
          healthStatus: redirector.health?.isOnline ? 'healthy' : 'offline',
          responseTime: redirector.health?.responseTime,
          radius: 22,
          data: redirector
        });
      });
    }

    // Beacons
    beacons.forEach(beacon => {
      nodes.push({
        id: `beacon-${beacon._id}`,
        label: beacon.systemInfo?.hostname || beacon.beaconId,
        type: beacon.type || 'http',
        isAlive: beacon.isActive && isBeaconAlive(beacon),
        lastSeen: beacon.lastSeen,
        failoverConfig: beacon.failoverConfig,
        radius: 20,
        evasion: {
          amsi: false, // Will be updated from backend
          etw: false
        },
        data: beacon
      });
    });

    return nodes;
  };

  const prepareLinks = (beacons, listeners, redirectors, showRedirectors) => {
    const links = [];

    // Server to Listeners
    listeners.forEach(listener => {
      links.push({
        source: 'c2-server',
        target: `listener-${listener._id}`,
        protocol: listener.type.toUpperCase(),
        port: listener.config?.port,
        bandwidth: listener.isActive ? 4 : 2,
        linkType: 'primary'
      });
    });

    // Redirectors to Listeners (if redirectors exist and shown)
    if (showRedirectors && redirectors) {
      redirectors.forEach(redirector => {
        // Find target (listener or C2)
        if (redirector.forwardTo?.type === 'c2') {
          // Redirector → C2 (shouldn't normally happen, but handle it)
          links.push({
            source: `redirector-${redirector._id}`,
            target: 'c2-server',
            protocol: 'HTTPS',
            port: 5000,
            bandwidth: 3,
            linkType: 'primary'
          });
        } else {
          // Redirector → Listener (most common)
          const listener = listeners.find(l => l._id === redirector.listener);
          if (listener) {
            links.push({
              source: `redirector-${redirector._id}`,
              target: `listener-${listener._id}`,
              protocol: listener.type.toUpperCase(),
              port: listener.config?.port,
              bandwidth: redirector.health?.isOnline ? 3 : 1,
              linkType: 'primary'
            });
          }
        }
      });
    }

    // Beacons to Redirectors or Listeners
    beacons.forEach(beacon => {
      if (beacon.parent) {
        // P2P beacon (child) - connects to parent beacon
        links.push({
          source: `beacon-${beacon.parent}`,
          target: `beacon-${beacon._id}`,
          protocol: 'SMB',
          bandwidth: 3,
          linkType: 'primary'
        });
      } else {
        // Check if beacon has failover config with redirector URLs
        if (showRedirectors && beacon.failoverConfig?.urls && beacon.failoverConfig.urls.length > 0) {
          // Beacon uses redirectors - find matching redirectors by URL
          beacon.failoverConfig.urls.forEach((url, index) => {
            const redirector = redirectors?.find(r => {
              const rUrl = `${r.forwardTo?.targetUrl || ''}`;
              return url.includes(r.hostname) || rUrl.includes(r.hostname);
            });

            if (redirector) {
              links.push({
                source: `beacon-${beacon._id}`,
                target: `redirector-${redirector._id}`,
                protocol: beacon.type?.toUpperCase() || 'HTTPS',
                bandwidth: isBeaconAlive(beacon) ? 3 : 1,
                linkType: index === 0 ? 'primary' : 'backup',
                priority: index
              });
            }
          });
        } else {
          // No redirectors - connect directly to listener
          const listener = listeners.find(l => l.type === (beacon.type || 'https'));
          if (listener) {
            links.push({
              source: `beacon-${beacon._id}`,
              target: `listener-${listener._id}`,
              protocol: beacon.type?.toUpperCase() || 'HTTPS',
              port: listener.config?.port,
              bandwidth: isBeaconAlive(beacon) ? 3 : 1,
              linkType: 'primary'
            });
          } else {
            // No listener found - connect beacon directly to server (fallback)
            links.push({
              source: `beacon-${beacon._id}`,
              target: 'c2-server',
              protocol: beacon.type?.toUpperCase() || 'HTTPS',
              port: 5000,
              bandwidth: isBeaconAlive(beacon) ? 3 : 1,
              linkType: 'primary'
            });
          }
        }
      }
    });

    return links;
  };

  const createForceSimulation = (nodes, links, width, height) => {
    return d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(150))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => (d.radius || 20) + 10));
  };

  const applyTreeLayout = (nodes, links, width, height) => {
    // Hierarchical tree layout
    const root = d3.stratify()
      .id(d => d.id)
      .parentId(d => {
        const link = links.find(l => l.target === d.id);
        return link ? link.source : null;
      })(nodes);

    const treeLayout = d3.tree()
      .size([width - 100, height - 100]);

    treeLayout(root);

    root.descendants().forEach(d => {
      const node = nodes.find(n => n.id === d.id);
      if (node) {
        node.x = d.x + 50;
        node.y = d.y + 50;
      }
    });
  };

  const applyRadialLayout = (nodes, links, width, height) => {
    // Radial layout (server in center, beacons in circle)
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 3;

    nodes.forEach((node, i) => {
      if (node.id === 'c2-server') {
        node.x = centerX;
        node.y = centerY;
      } else {
        const angle = (2 * Math.PI * i) / (nodes.length - 1);
        node.x = centerX + radius * Math.cos(angle);
        node.y = centerY + radius * Math.sin(angle);
      }
    });
  };

  const animateTraffic = (g, links) => {
    const traffic = g.append('g')
      .attr('class', 'traffic')
      .selectAll('circle')
      .data(links)
      .join('circle')
      .attr('r', 4)
      .attr('fill', '#0080ff')
      .attr('opacity', 0.9)
      .style('filter', 'drop-shadow(0 0 4px rgba(0, 128, 255, 0.8))');

    function animate() {
      traffic
        .attr('cx', d => d.source.x)
        .attr('cy', d => d.source.y)
        .transition()
        .duration(2000)
        .ease(d3.easeLinear)
        .attr('cx', d => d.target.x)
        .attr('cy', d => d.target.y)
        .attr('opacity', 0)
        .on('end', function(d) {
          d3.select(this).attr('opacity', 0.8);
          animate();
        });
    }

    animate();
  };

  const drag = (simulation) => {
    function dragstarted(event) {
      if (!event.active && simulation) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event) {
      if (!event.active && simulation) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    return d3.drag()
      .on('start', dragstarted)
      .on('drag', dragged)
      .on('end', dragended);
  };

  const getNodeColor = (node) => {
    // Professional color scheme
    if (node.type === 'server') return '#003d82'; // Dark blue
    if (node.type === 'redirector') return '#FF9800'; // Orange
    if (node.type === 'dns') return '#8b5cf6'; // Purple
    if (node.type === 'smb') return '#f59e0b'; // Orange
    if (node.type === 'https' || node.type === 'http') return '#10b981'; // Green
    return '#4d94ff'; // Light blue
  };

  const getNodeIcon = (node) => {
    // FontAwesome icons ONLY - NO EMOJIS
    if (node.type === 'server') return '\uf233'; // fa-server
    if (node.type === 'redirector') return '\uf0ec'; // fa-exchange
    if (node.type === 'dns') return '\uf0ac'; // fa-globe
    if (node.type === 'smb') return '\uf07b'; // fa-folder
    if (node.type === 'https' || node.type === 'http') return '\uf0c1'; // fa-link
    return '\uf108'; // fa-desktop
  };

  const getTypeBadgeColor = (node) => {
    if (node.type === 'server') return '#001f3f';
    if (node.type === 'dns') return '#003d82';
    if (node.type === 'smb') return '#004080';
    if (node.type === 'https') return '#0052cc';
    return '#1a75ff';
  };

  const getLinkColor = (link) => {
    return link.bandwidth > 2 ? '#0066cc' : '#4b5563';
  };

  const isBeaconAlive = (beacon) => {
    if (!beacon.lastSeen) return false;
    const timeout = (beacon.config?.sleep || 60) * 3 * 1000; // 3x sleep interval
    return (Date.now() - new Date(beacon.lastSeen).getTime()) < timeout;
  };

  const showTooltip = (event, d) => {
    if (!tooltipRef.current || !svgRef.current) return;

    const tooltip = tooltipRef.current;
    const svgRect = svgRef.current.getBoundingClientRect();

    // Get the current zoom transform
    const transform = d3.zoomTransform(svgRef.current);

    // Calculate the actual screen position of the node
    const nodeScreenX = svgRect.left + (d.x * transform.k) + transform.x;
    const nodeScreenY = svgRect.top + (d.y * transform.k) + transform.y;

    tooltip.style.display = 'block';
    // Position tooltip near the node with a small offset
    tooltip.style.left = `${nodeScreenX + 25}px`;
    tooltip.style.top = `${nodeScreenY - 10}px`;

    // Build tooltip content
    let html = `<div style="font-weight: bold; margin-bottom: 8px;">${d.label}</div>`;

    if (d.type === 'server') {
      html += `<div style="color: #60a5fa; margin-bottom: 4px;">C2 Server</div>`;
      html += `<div style="color: #9ca3af; font-size: 11px;">Central Command & Control</div>`;
    } else if (d.type === 'redirector') {
      // Redirector tooltip
      const redirector = d.data;

      // Status
      const statusColor = d.isAlive ? '#10b981' : '#ef4444';
      const statusText = d.isAlive ? 'Online' : 'Offline';
      html += `<div style="color: ${statusColor}; font-weight: bold; margin-bottom: 4px;">${statusText}</div>`;

      html += `<div style="color: #FF9800; margin-bottom: 4px;">Redirector</div>`;

      // Connection info
      html += `<div style="border-top: 1px solid #374151; margin-top: 8px; padding-top: 8px; font-size: 11px;">`;
      if (d.hostname) html += `<div style="color: #d1d5db;">Hostname: ${d.hostname}</div>`;
      if (redirector?.config?.port) html += `<div style="color: #d1d5db;">Port: ${redirector.config.port}</div>`;
      if (redirector?.type) html += `<div style="color: #d1d5db;">Type: ${redirector.type}</div>`;
      html += `</div>`;

      // Health info
      if (d.responseTime !== undefined) {
        html += `<div style="border-top: 1px solid #374151; margin-top: 8px; padding-top: 8px; font-size: 11px;">`;
        html += `<div style="color: #d1d5db;">Response Time: ${d.responseTime}ms</div>`;
        html += `</div>`;
      }

      // Health status details
      if (redirector?.health) {
        html += `<div style="border-top: 1px solid #374151; margin-top: 8px; padding-top: 8px; font-size: 11px;">`;
        if (redirector.health.lastCheck) {
          const lastCheck = new Date(redirector.health.lastCheck);
          html += `<div style="color: #9ca3af;">Last Check: ${lastCheck.toLocaleTimeString()}</div>`;
        }
        if (redirector.health.errors && redirector.health.errors.length > 0) {
          html += `<div style="color: #ef4444; margin-top: 4px;">Recent Error: ${redirector.health.errors[0]}</div>`;
        }
        html += `</div>`;
      }
    } else if (d.type === 'dns' || d.type === 'smb' || d.type === 'http' || d.type === 'https') {
      // Check if this is a listener or beacon by examining the data
      if (d.data && d.data.config && d.data.isActive !== undefined) {
        // This is a listener
        const listener = d.data;

        // Status
        const statusColor = d.isAlive ? '#10b981' : '#ef4444';
        const statusText = d.isAlive ? 'Running' : 'Stopped';
        html += `<div style="color: ${statusColor}; font-weight: bold; margin-bottom: 4px;">${statusText}</div>`;

        html += `<div style="color: #8b5cf6; margin-bottom: 4px;">${d.type.toUpperCase()} Listener</div>`;

        // Connection info
        html += `<div style="border-top: 1px solid #374151; margin-top: 8px; padding-top: 8px; font-size: 11px;">`;
        if (d.port) html += `<div style="color: #d1d5db;">Port: ${d.port}</div>`;
        if (listener.config?.endpoint) html += `<div style="color: #d1d5db;">Endpoint: ${listener.config.endpoint}</div>`;
        if (listener.config?.dnsZone) html += `<div style="color: #d1d5db;">DNS Zone: ${listener.config.dnsZone}</div>`;
        html += `</div>`;

        // Health status
        if (d.healthStatus) {
          const healthColor = d.healthStatus === 'healthy' ? '#10b981' : d.healthStatus === 'unhealthy' ? '#f59e0b' : '#9ca3af';
          html += `<div style="border-top: 1px solid #374151; margin-top: 8px; padding-top: 8px; font-size: 11px;">`;
          html += `<div style="color: ${healthColor};">Health: ${d.healthStatus}</div>`;
          if (listener.lastHeartbeat) {
            const lastBeat = new Date(listener.lastHeartbeat);
            html += `<div style="color: #9ca3af;">Last Heartbeat: ${lastBeat.toLocaleTimeString()}</div>`;
          }
          html += `</div>`;
        }
      } else if (d.data) {
        // This is a beacon
        const beacon = d.data;

        // Status
        const statusColor = d.isAlive ? '#10b981' : '#ef4444';
        const statusText = d.isAlive ? 'Online' : 'Offline';
        html += `<div style="color: ${statusColor}; font-weight: bold; margin-bottom: 4px;">${statusText}</div>`;

        // Approval status
        if (beacon.isApproved === false) {
          html += `<div style="color: #f59e0b; font-weight: bold; margin-bottom: 4px;">! Awaiting Approval</div>`;
        } else if (beacon.isApproved) {
          html += `<div style="color: #10b981; font-size: 11px; margin-bottom: 4px;">✓ Approved</div>`;
        }

        // Type
        html += `<div style="color: #8b5cf6; margin-bottom: 4px;">${(d.type || 'HTTP').toUpperCase()} Beacon</div>`;

        // System info
        if (beacon.systemInfo) {
          html += `<div style="border-top: 1px solid #374151; margin-top: 8px; padding-top: 8px; font-size: 11px;">`;
          if (beacon.systemInfo.hostname) html += `<div style="color: #d1d5db;">Host: ${beacon.systemInfo.hostname}</div>`;
          if (beacon.systemInfo.username) html += `<div style="color: #d1d5db;">User: ${beacon.systemInfo.username}</div>`;
          if (beacon.systemInfo.os) html += `<div style="color: #d1d5db;">OS: ${beacon.systemInfo.os}</div>`;
          if (beacon.systemInfo.ip) html += `<div style="color: #d1d5db;">IP: ${beacon.systemInfo.ip}</div>`;
          html += `</div>`;
        }

        // Last seen
        if (beacon.lastSeen) {
          const lastSeenDate = new Date(beacon.lastSeen);
          const timeAgo = Math.floor((Date.now() - lastSeenDate.getTime()) / 1000);
          let timeStr;
          if (timeAgo < 60) timeStr = `${timeAgo}s ago`;
          else if (timeAgo < 3600) timeStr = `${Math.floor(timeAgo / 60)}m ago`;
          else if (timeAgo < 86400) timeStr = `${Math.floor(timeAgo / 3600)}h ago`;
          else timeStr = `${Math.floor(timeAgo / 86400)}d ago`;

          html += `<div style="color: #9ca3af; font-size: 11px; margin-top: 4px;">Last seen: ${timeStr}</div>`;
        }

        // Failover config
        if (d.failoverConfig && d.failoverConfig.urls && d.failoverConfig.urls.length > 1) {
          html += `<div style="border-top: 1px solid #374151; margin-top: 8px; padding-top: 8px; font-size: 11px;">`;
          html += `<div style="color: #10b981; font-weight: bold;">Failover Config:</div>`;
          html += `<div style="color: #d1d5db;">Strategy: ${d.failoverConfig.strategy}</div>`;
          html += `<div style="color: #d1d5db;">Redirectors: ${d.failoverConfig.urls.length}</div>`;
          if (d.failoverConfig.currentIndex !== undefined) {
            html += `<div style="color: #d1d5db;">Current: #${d.failoverConfig.currentIndex + 1}</div>`;
          }
          html += `</div>`;
        }

        // Evasion capabilities
        if (beacon.evasion && (beacon.evasion.amsi || beacon.evasion.etw)) {
          html += `<div style="border-top: 1px solid #374151; margin-top: 8px; padding-top: 8px; font-size: 11px;">`;
          html += `<div style="color: #10b981; font-weight: bold;">Evasion Active:</div>`;
          if (beacon.evasion.amsi) html += `<div style="color: #10b981;">✓ AMSI Bypass</div>`;
          if (beacon.evasion.etw) html += `<div style="color: #10b981;">✓ ETW Bypass</div>`;
          html += `</div>`;
        }
      }
    }

    tooltip.innerHTML = html;
  };

  const hideTooltip = () => {
    if (!tooltipRef.current) return;
    tooltipRef.current.style.display = 'none';
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      keyboard={true}
      animation={false}
    >
      <Modal.Header closeButton style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151', color: '#fff' }}>
        <Modal.Title style={{ color: '#fff' }}>
          <i className="fas fa-project-diagram me-2"></i>
          Network Topology
          <Badge bg="secondary" className="ms-2">{beacons.length} beacons</Badge>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: '#1f2937', padding: 0 }}>
        <div className="d-flex justify-content-center p-3" style={{ backgroundColor: '#111827', borderBottom: '1px solid #374151' }}>
          <ButtonGroup size="sm">
            <Button
              variant={layout === 'force' ? 'primary' : 'outline-primary'}
              onClick={() => setLayout('force')}
            >
              <i className="fas fa-atom me-1"></i> Force
            </Button>
            <Button
              variant={layout === 'tree' ? 'primary' : 'outline-primary'}
              onClick={() => setLayout('tree')}
            >
              <i className="fas fa-sitemap me-1"></i> Tree
            </Button>
            <Button
              variant={layout === 'radial' ? 'primary' : 'outline-primary'}
              onClick={() => setLayout('radial')}
            >
              <i className="fas fa-circle-notch me-1"></i> Radial
            </Button>
            <Button
              variant={showTraffic ? 'success' : 'outline-success'}
              onClick={() => setShowTraffic(!showTraffic)}
            >
              <i className="fas fa-exchange-alt me-1"></i> Traffic
            </Button>
          </ButtonGroup>
        </div>

        <div style={{ height: '600px', position: 'relative', backgroundColor: '#1f2937' }}>
          <svg ref={svgRef} width="100%" height="100%"></svg>
          <div
            ref={tooltipRef}
            style={{
              display: 'none',
              position: 'absolute',
              background: 'rgba(0, 0, 0, 0.95)',
              color: 'white',
              padding: '12px',
              borderRadius: '8px',
              fontSize: '12px',
              pointerEvents: 'none',
              maxWidth: '300px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              zIndex: 9999,
              border: '1px solid #374151'
            }}
          />
        </div>

        <style>{`
          .node-alive circle {
            filter: drop-shadow(0 0 8px rgba(16, 185, 129, 0.8));
          }

          .node-dead circle {
            filter: grayscale(1);
            opacity: 0.4;
          }

          .node-unapproved circle {
            filter: drop-shadow(0 0 8px rgba(245, 158, 11, 0.8));
          }

          .link-label {
            font-family: monospace;
            font-weight: bold;
            text-shadow: 1px 1px 3px rgba(0, 0, 0, 1), 0 0 5px rgba(0, 0, 0, 0.8);
          }

          .node {
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .node:hover circle {
            filter: brightness(1.3) drop-shadow(0 0 12px rgba(255, 200, 0, 0.9));
          }
        `}</style>
      </Modal.Body>
    </Modal>
  );
};

export default EnhancedNetworkGraph;
